/**
 * Nmbrs Leave Overview
 *
 * Displays leave records (withdrawals) per employee in a sortable table.
 * Story 3.2: Verlofoverzicht
 *
 * Design system colors:
 * - Navy #2F5A7A: headers, primary
 * - Teal #54949E: pending status badge
 * - Green #B7D945: approved status badge
 * - Red #D64545: rejected status badge
 * - Border #E2E8F0: table borders, dividers
 */

import { useState, useMemo } from 'react';
import { useLinkedEmployees } from '../hooks/useNmbrs';
import { useLeaveList } from '../hooks/useNmbrsLeave';
import type { NmbrsLeaveRecord } from '../hooks/useNmbrsLeave';

// ============================================
// Constants
// ============================================

const DEFAULT_HOURS_PER_DAY = 8;

// ============================================
// Helpers
// ============================================

/** Format ISO date string to Dutch locale. */
function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// ============================================
// Sub-components
// ============================================

/** Status badge with design system colors. */
function StatusBadge({ status }: { status: string }) {
  let bgColor: string;
  let textColor: string;
  let label: string;

  switch (status) {
    case 'Approved':
      bgColor = 'bg-[#B7D945]/20';
      textColor = 'text-[#2F5A7A]';
      label = 'Goedgekeurd';
      break;
    case 'Rejected':
      bgColor = 'bg-[#D64545]/10';
      textColor = 'text-[#D64545]';
      label = 'Afgewezen';
      break;
    case 'Pending':
    default:
      bgColor = 'bg-[#54949E]/15';
      textColor = 'text-[#54949E]';
      label = 'Aangevraagd';
      break;
  }

  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${bgColor} ${textColor}`}>
      {label}
    </span>
  );
}

/** Year selector dropdown. */
function YearSelector({
  year,
  onChange,
}: {
  year: number;
  onChange: (year: number) => void;
}) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - 4 + i);

  return (
    <select
      value={year}
      onChange={(e) => onChange(Number(e.target.value))}
      className="px-3 py-1.5 border border-[#E2E8F0] rounded-lg text-sm text-[#1E293B] focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E]"
    >
      {years.map((y) => (
        <option key={y} value={y}>
          {y}
        </option>
      ))}
    </select>
  );
}

/** Loading skeleton for the table. */
function TableSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="border-t border-[#E2E8F0]">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3 border-b border-[#E2E8F0]">
            <div className="h-4 bg-gray-200 rounded w-24" />
            <div className="h-4 bg-gray-200 rounded w-20" />
            <div className="h-4 bg-gray-200 rounded w-20" />
            <div className="h-4 bg-gray-200 rounded w-12" />
            <div className="h-4 bg-gray-200 rounded w-12" />
            <div className="h-4 bg-gray-200 rounded w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Sort direction indicator. */
function SortIndicator({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) {
  if (!active) {
    return (
      <svg className="w-3 h-3 ml-1 text-gray-300 inline-block" fill="currentColor" viewBox="0 0 20 20">
        <path d="M5 8l5-5 5 5H5zM5 12l5 5 5-5H5z" />
      </svg>
    );
  }
  return (
    <svg className="w-3 h-3 ml-1 text-[#2F5A7A] inline-block" fill="currentColor" viewBox="0 0 20 20">
      {direction === 'asc' ? (
        <path d="M5 12l5-5 5 5H5z" />
      ) : (
        <path d="M5 8l5 5 5-5H5z" />
      )}
    </svg>
  );
}

// ============================================
// Leave Table
// ============================================

type SortField = 'startDate' | 'endDate' | 'type' | 'hours' | 'status';

interface LeaveTableProps {
  leaves: NmbrsLeaveRecord[];
  hoursPerDay: number;
  year: number;
}

function LeaveTable({ leaves, hoursPerDay, year }: LeaveTableProps) {
  const [sortField, setSortField] = useState<SortField>('startDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'startDate' ? 'desc' : 'asc');
    }
  };

  const sortedLeaves = useMemo(() => {
    return [...leaves].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'startDate':
          cmp = (a.startDate || '').localeCompare(b.startDate || '');
          break;
        case 'endDate':
          cmp = (a.endDate || '').localeCompare(b.endDate || '');
          break;
        case 'type':
          cmp = a.type.localeCompare(b.type);
          break;
        case 'hours':
          cmp = a.hours - b.hours;
          break;
        case 'status':
          cmp = a.status.localeCompare(b.status);
          break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [leaves, sortField, sortDirection]);

  if (leaves.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-gray-500">
          Geen verlofaanvragen gevonden voor {year}
        </p>
      </div>
    );
  }

  const headerClass =
    'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-[#2F5A7A] transition-colors';

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-[#E2E8F0]">
        <thead className="bg-gray-50">
          <tr>
            <th className={headerClass} onClick={() => handleSort('type')}>
              Type
              <SortIndicator active={sortField === 'type'} direction={sortDirection} />
            </th>
            <th className={headerClass} onClick={() => handleSort('startDate')}>
              Startdatum
              <SortIndicator active={sortField === 'startDate'} direction={sortDirection} />
            </th>
            <th className={headerClass} onClick={() => handleSort('endDate')}>
              Einddatum
              <SortIndicator active={sortField === 'endDate'} direction={sortDirection} />
            </th>
            <th className={headerClass} onClick={() => handleSort('hours')}>
              Uren
              <SortIndicator active={sortField === 'hours'} direction={sortDirection} />
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Dagen
            </th>
            <th className={headerClass} onClick={() => handleSort('status')}>
              Status
              <SortIndicator active={sortField === 'status'} direction={sortDirection} />
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-[#E2E8F0]">
          {sortedLeaves.map((leave) => (
            <tr key={leave.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-4 py-3 text-sm font-medium text-[#1E293B]">
                {leave.type}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {formatDate(leave.startDate)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {formatDate(leave.endDate)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {leave.hours}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {Math.round((leave.hours / hoursPerDay) * 10) / 10}
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={leave.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function NmbrsLeaveOverview() {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number>(0);
  const [year, setYear] = useState(new Date().getFullYear());

  const { data: linksData, isLoading: linksLoading } = useLinkedEmployees();
  const { data: leaveData, isLoading: leaveLoading, error: leaveError, refetch } = useLeaveList(
    selectedEmployeeId,
    year
  );

  const linkedEmployees = linksData?.data ?? [];
  const leaves = leaveData?.data?.leaves ?? [];
  const hoursPerDay = leaveData?.data?.hoursPerDay ?? DEFAULT_HOURS_PER_DAY;

  // Check for "not linked" error
  const isNotLinked =
    leaveError instanceof Error &&
    (leaveError.message.includes('EMPLOYEE_NOT_LINKED') ||
      leaveError.message.includes('not linked'));

  const isError = leaveError && !isNotLinked;

  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#E2E8F0] flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-[#2F5A7A]">Verlofoverzicht</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Verlofaanvragen per medewerker vanuit Nmbrs
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Employee selector */}
          <select
            value={selectedEmployeeId}
            onChange={(e) => setSelectedEmployeeId(Number(e.target.value))}
            disabled={linksLoading}
            className="px-3 py-1.5 border border-[#E2E8F0] rounded-lg text-sm text-[#1E293B] focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E] min-w-[180px]"
          >
            <option value={0}>Selecteer medewerker</option>
            {linkedEmployees.map((link) => (
              <option key={link.employeeId} value={link.employeeId}>
                {link.employeeName}
              </option>
            ))}
          </select>

          {/* Year selector */}
          <YearSelector year={year} onChange={setYear} />
        </div>
      </div>

      {/* Content */}
      {selectedEmployeeId === 0 && (
        <div className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
          <p className="text-sm text-gray-500">
            Selecteer een medewerker om verlofaanvragen te bekijken.
          </p>
        </div>
      )}

      {selectedEmployeeId > 0 && leaveLoading && <TableSkeleton />}

      {selectedEmployeeId > 0 && isNotLinked && (
        <div className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          </div>
          <p className="text-sm text-gray-500">Niet gekoppeld aan Nmbrs</p>
          <p className="text-xs text-gray-400 mt-1">
            Koppel deze medewerker eerst aan een Nmbrs-account om verlofaanvragen te bekijken.
          </p>
        </div>
      )}

      {selectedEmployeeId > 0 && isError && (
        <div className="p-6 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-50 mb-3">
            <svg className="w-6 h-6 text-[#D64545]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <p className="text-sm text-[#1E293B] mb-3">
            {leaveError instanceof Error ? leaveError.message : 'Onbekende fout'}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 text-sm font-medium text-white bg-[#2F5A7A] rounded-lg hover:bg-[#254a65] transition-colors"
          >
            Opnieuw proberen
          </button>
        </div>
      )}

      {selectedEmployeeId > 0 && !leaveLoading && !leaveError && (
        <LeaveTable leaves={leaves} hoursPerDay={hoursPerDay} year={year} />
      )}
    </div>
  );
}
